/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Box } from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';
import { get, last, toLower } from 'lodash';
import isNil from 'lodash/isNil';
import styled, { ThemeProvider } from 'styled-components';
import { dateToUTCDate, dateToUTCTime } from '../../../../../utils/DateTimeUtils';
import StyledProps from '../../../../common/theme/AppThemeInterface';
import appTheme from '../../../../common/theme/appTheme.json';
import { ChartType } from '../ChartSeriesItems';
import ChartsSeriesColorWrapper from '../ChartsSeriesColorWrapper';
import ChartCustomTooltipHeader from './ChartCustomTooltipHeader';
import ChartCustomTooltipSymbol from './ChartCustomTooltipSymbol';
import ChartLegendTooltipContent, { ChartLegendTooltipEntry } from './ChartLegendTooltipContent';

const TOOLTIP_BODY_ITEM_HEIGHT = '16px';
export const LEGEND_ITEM_ICON_WIDTH = 21;
const MIN_TO_MS = 60000;
export interface ChartCustomTooltipProps {
  data: any;
  sortByYValue: boolean;
  showGraphs?: boolean;
}

const ChartCustomTooltip = (props: ChartCustomTooltipProps) => {
  const { data, sortByYValue, showGraphs } = props;
  const { points, x } = data;
  let displayPoints = points.slice();
  // get the closest points to the currently hovered timestamp for all the series
  points[0].series.chart.series.forEach((series: any, index: number) => {
    // only add the closest point if the point is not already present or is not visible
    if (!points.find((tempPoint: any) => tempPoint.point.series.name === series.name || !series.visible)) {
      let distance = Number.MAX_VALUE;
      let pointToAdd;

      series.data.forEach((dataPoint: any) => {
        let diff = Math.abs(x - dataPoint.x);
        if (diff < distance && diff < MIN_TO_MS) {
          distance = diff;
          pointToAdd = dataPoint;
        }
      });

      if (!isNil(pointToAdd)) {
        series.setState('normal');
        displayPoints.splice(index, 0, pointToAdd);
      }
    }
  });

  // get the custom metadata labels for all the series in the chart
  // these will be shown underneath the timestamp in the tooltip
  let foundMetadata = false;
  let customMetadataLabelDivider = '';
  let customMetadataLabels = displayPoints
    .reduce((accumLabels: any[], point: any) => {
      if (point.series.options.custom && point.series.options.custom.metaData) {
        foundMetadata = true;
        point.series.options.custom.metaData.forEach((metadata: any) => {
          customMetadataLabelDivider = point.series.options.custom.metadataLabelDivider;
          const label = metadata.call({
            points: point,
            primaryTerminalHistory: point.series.options.custom.getPrimaryTerminalHistory?.(),
            secondaryTerminalHistory: point.series.options.custom.getSecondaryTerminalHistory?.(),
            selectedTerminalTabIndex: point.series.options.custom.selectedTerminalTabIndex,
          });

          if (!isNil(label) && !accumLabels.find(l => l === label)) {
            accumLabels.push(label);
          }
        });
      }

      return accumLabels;
    }, [])
    .filter((s: string) => s.length > 0);

  // insert label divider symbol if there are two labels
  if (foundMetadata && customMetadataLabels.length === 2) {
    customMetadataLabels.splice(1, 0, customMetadataLabelDivider);
  }
  const totalUsage = displayPoints.reduce((prev: number, curr: any) => prev + curr.y, 0) || 0;

  if (sortByYValue) {
    const displayPointsWithoutOther = displayPoints
      .filter((point: any) => toLower(point?.point?.series?.name) !== 'other')
      .sort((p1: any, p2: any) => get(p2, 'y', 0) - get(p1, 'y', 0));

    // If other Exists, add it last
    const otherCategory = displayPoints.find((point: any) => toLower(point?.point?.series?.name) === 'other');
    if (!isNil(otherCategory)) {
      displayPoints = [...displayPointsWithoutOther, otherCategory];
    } else {
      displayPoints = displayPointsWithoutOther;
    }
    displayPoints = displayPoints.filter((point: any) => point?.y);
  } else {
    // Sort points so that they display by color
    displayPoints.sort((p1: any, p2: any) => get(p1, 'colorIndex', 0) - get(p2, 'colorIndex', 0));
  }

  return (
    <ChartsSeriesColorWrapper>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={appTheme}>
          <StyledWrapper className="chart__custom__tooltip">
            <ChartCustomTooltipHeader>
              <Box sx={{ color: appTheme.secondaryHeaderTextColor, fontSize: '12px', lineHeight: '20px' }}>
                {dateToUTCDate(new Date(data.x))}
              </Box>
              <Box sx={{ color: '#ffffff', fontSize: '20px', lineHeight: '20px', fontWeight: 700 }}>
                {dateToUTCTime(new Date(data.x))}
              </Box>

              <Box>{customMetadataLabels}</Box>
            </ChartCustomTooltipHeader>
            <StyledTooltipBody>
              <ChartLegendTooltipContent
                entries={displayPoints.map(
                  (point: any) =>
                    ({
                      delimitedLastIndexDisplayName: last(point.series.userOptions.name.split('/')),
                      nonDelimintedName: point.series.userOptions.name,
                      value: point.y,
                      dislayValue: point.series.tooltipOptions.pointFormatter
                        ? point.series.tooltipOptions.pointFormatter.call(point)
                        : null,
                      icon: point.series.options.custom.useDetailedLabelSymbols ? (
                        <Box sx={{ height: '16px', width: '12px' }}>
                          <ChartCustomTooltipSymbol
                            chartType={point.series.userOptions.type as ChartType}
                            className={point.series.userOptions.className}
                          />
                        </Box>
                      ) : (
                        <Box sx={{ height: '12px', width: '12px' }}>
                          <svg viewBox="0 0 12 12" stroke="none" className={point.series.userOptions.className}>
                            <circle r={6} cx={6} cy={6} stroke="none" stroke-width="0"></circle>
                          </svg>
                        </Box>
                      ),
                    } as ChartLegendTooltipEntry),
                )}
                totalUsage={totalUsage}
                showGraphs={showGraphs}
                showIcons
              />
            </StyledTooltipBody>
            <StyledTooltipPointerRightContainer>
              <StyledTooltipPointer />
            </StyledTooltipPointerRightContainer>
          </StyledWrapper>
        </ThemeProvider>
      </StyledEngineProvider>
    </ChartsSeriesColorWrapper>
  );
};
export default ChartCustomTooltip;

const StyledWrapper = styled.div`
  overflow: visible;
  position: relative;
  background-color: ${({ theme }: StyledProps) => theme.secondaryBackgroundColor};
  border-radius: 8px;
  overflow: hidden;
`;

export const StyledTooltipBody = styled.div`
  display: flex;
  padding: 10px 15px;
  box-sizing: border-box;
  background-color: #000000;
  z-index: 5;
`;
export const StyledSymbolContainer = styled.div`
  position: relative;
  width: ${LEGEND_ITEM_ICON_WIDTH}px;
  height: ${TOOLTIP_BODY_ITEM_HEIGHT};
  margin-right: 5px;
`;
export const StyledLabel = styled.div`
  font-size: 14px;
  line-height: ${TOOLTIP_BODY_ITEM_HEIGHT};
  margin-right: 5px;
  color: ${({ theme, primary }: StyledProps & { primary: boolean }) =>
    primary ? theme.primaryTextColor : theme.grayTextColor};
  font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
  font-weight: ${({ bold }: { bold: boolean }) => (bold ? 700 : 400)};
  text-transform: capitalize;
`;

const StyledTooltipPointerRightContainer = styled.div`
  height: 100%;
  width: 5px;
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  top: 0px;
  right: 0px;
  z-index: -1;
`;
const StyledTooltipPointer = styled.div`
  background-color: ${({ theme }: StyledProps) => theme.secondaryBackgroundColor};
  width: 10px;
  height: 10px;
  transform: rotate(45deg);
`;
